import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { platformAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../components/ui/tabs';
import BreakGlassBanner from '../components/BreakGlassBanner';
import PlatformBreakGlass from '../components/platform/PlatformBreakGlass';
import PlatformLeads from '../components/platform/PlatformLeads';
import { toast } from 'sonner';
import {
  Shield,
  Building2,
  Users,
  Activity,
  Search,
  LogOut,
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Ban,
  UserCog,
  History,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const PLATFORM_ACCENT = '#F59E0B'; // Amber — intentionally different from tenant teal
const BG = '#0B0B0F'; // Deeper near-black so Platform is unmistakably not the tenant app
const PANEL = '#17171E';
const BORDER = '#2A2A33';

const StatusPill = ({ status }) => {
  const map = {
    trial: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30', label: 'Trial', Icon: Clock },
    active: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'Active', Icon: CheckCircle2 },
    expired: { bg: 'bg-rose-500/15', text: 'text-rose-400', border: 'border-rose-500/30', label: 'Expired', Icon: AlertTriangle },
    suspended: { bg: 'bg-zinc-500/20', text: 'text-zinc-300', border: 'border-zinc-500/40', label: 'Suspended', Icon: Ban },
  };
  const s = map[status] || map.expired;
  const Icon = s.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${s.bg} ${s.text} ${s.border}`}
      data-testid={`status-pill-${status}`}
    >
      <Icon className="h-3 w-3" />
      {s.label}
    </span>
  );
};

const Kpi = ({ label, value, Icon, tone = 'default' }) => (
  <Card
    className="border"
    style={{ background: PANEL, borderColor: BORDER }}
    data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
          <div className="mt-2 text-3xl font-bold text-white tabular-nums">{value}</div>
        </div>
        <div className="p-2 rounded-lg" style={{ background: tone === 'accent' ? `${PLATFORM_ACCENT}22` : '#ffffff10' }}>
          <Icon className="h-5 w-5" style={{ color: tone === 'accent' ? PLATFORM_ACCENT : '#A1A1AA' }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'd MMM yyyy'); } catch { return iso; }
};
const formatDateTime = (iso) => {
  if (!iso) return '—';
  try { return format(parseISO(iso), "d MMM yyyy, HH:mm"); } catch { return iso; }
};

export default function Platform() {
  const navigate = useNavigate();
  const { user, logout, isPlatformAdmin } = useAuth();

  const [tab, setTab] = useState('orgs');
  const [metrics, setMetrics] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [adminSearchQ, setAdminSearchQ] = useState('');
  const [adminSearchResults, setAdminSearchResults] = useState([]);
  const [audit, setAudit] = useState([]);

  const loadMetrics = useCallback(async () => {
    try {
      const { data } = await platformAPI.metrics();
      setMetrics(data);
    } catch (e) {
      toast.error('Failed to load platform metrics');
    }
  }, []);

  const loadOrgs = useCallback(async () => {
    setOrgsLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await platformAPI.listOrganisations(params);
      setOrgs(data.items || []);
    } catch (e) {
      toast.error('Failed to load organisations');
    } finally {
      setOrgsLoading(false);
    }
  }, [search, statusFilter]);

  const loadAdmins = useCallback(async () => {
    try {
      const { data } = await platformAPI.listAdmins();
      setAdmins(data.items || []);
    } catch (e) {
      toast.error('Failed to load platform admins');
    }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const { data } = await platformAPI.audit(200);
      setAudit(data.items || []);
    } catch (e) {
      toast.error('Failed to load platform audit log');
    }
  }, []);

  useEffect(() => {
    if (!user || !isPlatformAdmin()) {
      navigate('/dashboard', { replace: true });
      return;
    }
    loadMetrics();
    loadOrgs();
  }, [user, isPlatformAdmin, navigate, loadMetrics, loadOrgs]);

  useEffect(() => {
    if (tab === 'admins') loadAdmins();
    if (tab === 'audit') loadAudit();
  }, [tab, loadAdmins, loadAudit]);

  const openOrg = async (org) => {
    setSelectedOrg(org);
    setSelectedDetail(null);
    try {
      const { data } = await platformAPI.getOrganisation(org.id);
      setSelectedDetail(data);
    } catch (e) {
      toast.error('Failed to load organisation detail');
    }
  };

  const runUserSearch = async () => {
    if (adminSearchQ.trim().length < 2) {
      toast.message('Enter at least 2 characters to search');
      return;
    }
    try {
      const { data } = await platformAPI.searchUsers(adminSearchQ.trim());
      setAdminSearchResults(data.items || []);
    } catch (e) {
      toast.error('User search failed');
    }
  };

  const grant = async (userId) => {
    try {
      await platformAPI.grantAdmin(userId);
      toast.success('Platform admin granted');
      setAdminSearchResults((prev) => prev.map((u) => u.id === userId ? { ...u, is_platform_admin: true } : u));
      loadAdmins();
      loadMetrics();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Grant failed');
    }
  };

  const revoke = async (userId) => {
    try {
      await platformAPI.revokeAdmin(userId);
      toast.success('Platform admin revoked');
      loadAdmins();
      loadMetrics();
      setAdminSearchResults((prev) => prev.map((u) => u.id === userId ? { ...u, is_platform_admin: false } : u));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen" style={{ background: BG }} data-testid="platform-console">
      <BreakGlassBanner />
      {/* Platform header — intentionally different from tenant chrome */}
      <header className="border-b" style={{ background: '#0F0F15', borderColor: BORDER }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="h-9 w-9 rounded-lg flex items-center justify-center"
              style={{ background: `${PLATFORM_ACCENT}22`, border: `1px solid ${PLATFORM_ACCENT}55` }}
            >
              <Shield className="h-5 w-5" style={{ color: PLATFORM_ACCENT }} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: PLATFORM_ACCENT }}>
                Platform Operations
              </div>
              <div className="text-sm text-zinc-400">
                Signed in as <span className="text-zinc-200 font-medium">{user?.email}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-tenant-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to workspace
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
              onClick={handleLogout}
              data-testid="platform-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* KPIs */}
        <section>
          <h2 className="text-sm uppercase tracking-wider text-zinc-500 font-semibold mb-3">Platform Health</h2>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi label="Organisations" value={metrics?.orgs_total ?? '—'} Icon={Building2} tone="accent" />
            <Kpi label="Trial" value={metrics?.orgs_by_status?.trial ?? '—'} Icon={Clock} />
            <Kpi label="Active" value={metrics?.orgs_by_status?.active ?? '—'} Icon={CheckCircle2} />
            <Kpi label="Active last 7d" value={metrics?.orgs_active_last_7d ?? '—'} Icon={Activity} />
            <Kpi label="Users total" value={metrics?.users_total ?? '—'} Icon={Users} />
          </div>
        </section>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-transparent border-b border-zinc-800 rounded-none p-0 h-auto">
            <TabsTrigger
              value="orgs"
              className="data-[state=active]:bg-transparent data-[state=active]:text-amber-400 data-[state=active]:border-amber-400 data-[state=active]:shadow-none border-b-2 border-transparent rounded-none text-zinc-400 px-4 py-2"
              data-testid="tab-orgs"
            >
              Organisations
            </TabsTrigger>
            <TabsTrigger
              value="admins"
              className="data-[state=active]:bg-transparent data-[state=active]:text-amber-400 data-[state=active]:border-amber-400 data-[state=active]:shadow-none border-b-2 border-transparent rounded-none text-zinc-400 px-4 py-2"
              data-testid="tab-admins"
            >
              Platform Admins
            </TabsTrigger>
            <TabsTrigger
              value="audit"
              className="data-[state=active]:bg-transparent data-[state=active]:text-amber-400 data-[state=active]:border-amber-400 data-[state=active]:shadow-none border-b-2 border-transparent rounded-none text-zinc-400 px-4 py-2"
              data-testid="tab-audit"
            >
              Audit
            </TabsTrigger>
            <TabsTrigger
              value="breakglass"
              className="data-[state=active]:bg-transparent data-[state=active]:text-amber-400 data-[state=active]:border-amber-400 data-[state=active]:shadow-none border-b-2 border-transparent rounded-none text-zinc-400 px-4 py-2"
              data-testid="tab-breakglass"
            >
              Break-Glass
            </TabsTrigger>
            <TabsTrigger
              value="leads"
              className="data-[state=active]:bg-transparent data-[state=active]:text-amber-400 data-[state=active]:border-amber-400 data-[state=active]:shadow-none border-b-2 border-transparent rounded-none text-zinc-400 px-4 py-2"
              data-testid="tab-leads"
            >
              Leads
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orgs" className="mt-6 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadOrgs()}
                  placeholder="Search by name or slug…"
                  className="pl-9 bg-[#17171E] border-zinc-800 text-zinc-100 placeholder:text-zinc-600"
                  data-testid="orgs-search-input"
                />
              </div>
              <div className="flex gap-1">
                {['all', 'trial', 'active', 'expired', 'suspended'].map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant="outline"
                    onClick={() => setStatusFilter(s)}
                    className={`capitalize border-zinc-800 ${statusFilter === s ? 'bg-amber-500/15 text-amber-300 border-amber-500/40' : 'text-zinc-300 hover:bg-zinc-800'}`}
                    data-testid={`filter-${s}`}
                  >
                    {s}
                  </Button>
                ))}
              </div>
              <Button
                size="sm"
                onClick={loadOrgs}
                className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                data-testid="orgs-refresh-btn"
              >
                Refresh
              </Button>
            </div>

            <Card className="border" style={{ background: PANEL, borderColor: BORDER }}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-zinc-500 border-b border-zinc-800">
                      <th className="font-medium px-4 py-3">Organisation</th>
                      <th className="font-medium px-4 py-3">Status</th>
                      <th className="font-medium px-4 py-3">Owner</th>
                      <th className="font-medium px-4 py-3">Users</th>
                      <th className="font-medium px-4 py-3">Trial ends</th>
                      <th className="font-medium px-4 py-3">Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgsLoading && (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">Loading…</td></tr>
                    )}
                    {!orgsLoading && orgs.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No organisations match.</td></tr>
                    )}
                    {!orgsLoading && orgs.map((o) => (
                      <tr
                        key={o.id}
                        className="border-b border-zinc-900 hover:bg-zinc-900/50 cursor-pointer"
                        onClick={() => openOrg(o)}
                        data-testid={`org-row-${o.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-zinc-100">{o.name}</div>
                          <div className="text-xs text-zinc-500">{o.slug || o.id}</div>
                        </td>
                        <td className="px-4 py-3"><StatusPill status={o.status} /></td>
                        <td className="px-4 py-3 text-zinc-300">
                          {o.owner_email || <span className="text-zinc-600">—</span>}
                        </td>
                        <td className="px-4 py-3 text-zinc-200 tabular-nums">{o.user_count}</td>
                        <td className="px-4 py-3 text-zinc-300">
                          {formatDate(o.trial_ends_at)}
                          {o.trial_days_remaining !== null && o.trial_days_remaining !== undefined && (
                            <span className={`ml-2 text-xs ${o.trial_days_remaining < 0 ? 'text-rose-400' : o.trial_days_remaining <= 3 ? 'text-amber-400' : 'text-zinc-500'}`}>
                              ({o.trial_days_remaining}d)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-zinc-400 text-xs">{formatDateTime(o.last_activity_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="admins" className="mt-6 space-y-6">
            <Card className="border" style={{ background: PANEL, borderColor: BORDER }}>
              <CardContent className="p-5 space-y-4">
                <div>
                  <div className="text-sm font-semibold text-zinc-100 mb-1">Grant platform admin</div>
                  <div className="text-xs text-zinc-500 mb-3">
                    Search any user across tenants by email or name. Granting adds the platform-admin flag without changing their tenant role.
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={adminSearchQ}
                      onChange={(e) => setAdminSearchQ(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && runUserSearch()}
                      placeholder="email@example.com or name fragment"
                      className="bg-[#0F0F15] border-zinc-800 text-zinc-100 placeholder:text-zinc-600"
                      data-testid="user-search-input"
                    />
                    <Button
                      onClick={runUserSearch}
                      className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                      data-testid="user-search-btn"
                    >
                      <Search className="h-4 w-4 mr-1" /> Search
                    </Button>
                  </div>
                </div>
                {adminSearchResults.length > 0 && (
                  <div className="divide-y divide-zinc-900 border border-zinc-800 rounded-md">
                    {adminSearchResults.map((u) => (
                      <div key={u.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`search-result-${u.id}`}>
                        <div>
                          <div className="text-sm text-zinc-100">{u.name} <span className="text-zinc-500">· {u.email}</span></div>
                          <div className="text-xs text-zinc-500">role: {u.role} · org: {u.organisation_id || '—'}</div>
                        </div>
                        {u.is_platform_admin ? (
                          <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10"
                            onClick={() => revoke(u.id)} data-testid={`revoke-${u.id}`}>
                            Revoke
                          </Button>
                        ) : (
                          <Button size="sm" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                            onClick={() => grant(u.id)} data-testid={`grant-${u.id}`}>
                            Grant admin
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border" style={{ background: PANEL, borderColor: BORDER }}>
              <CardContent className="p-5">
                <div className="text-sm font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                  <UserCog className="h-4 w-4 text-amber-400" /> Current platform admins
                </div>
                <div className="divide-y divide-zinc-900">
                  {admins.length === 0 && (
                    <div className="text-sm text-zinc-500 py-4">No platform admins yet.</div>
                  )}
                  {admins.map((u) => (
                    <div key={u.id} className="flex items-center justify-between py-3" data-testid={`admin-row-${u.id}`}>
                      <div>
                        <div className="text-sm text-zinc-100">{u.name} <span className="text-zinc-500">· {u.email}</span></div>
                        <div className="text-xs text-zinc-500">role: {u.role} · org: {u.organisation_id || '—'}</div>
                      </div>
                      {u.id === user?.id ? (
                        <Badge variant="outline" className="border-amber-500/40 text-amber-400">You</Badge>
                      ) : (
                        <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10"
                          onClick={() => revoke(u.id)} data-testid={`admin-revoke-${u.id}`}>
                          Revoke
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audit" className="mt-6">
            <Card className="border" style={{ background: PANEL, borderColor: BORDER }}>
              <CardContent className="p-5">
                <div className="text-sm font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                  <History className="h-4 w-4 text-amber-400" /> Platform admin actions
                </div>
                <div className="divide-y divide-zinc-900">
                  {audit.length === 0 && (
                    <div className="text-sm text-zinc-500 py-4">No platform audit events yet.</div>
                  )}
                  {audit.map((row) => (
                    <div key={row.id} className="py-3 flex items-start gap-4" data-testid={`audit-row-${row.id}`}>
                      <Badge variant="outline" className="border-amber-500/40 text-amber-400 font-mono text-[11px]">
                        {row.action}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-200 truncate">
                          {row.user_name} <span className="text-zinc-500">· entity {row.entity_id || '—'}</span>
                        </div>
                        {row.details && Object.keys(row.details).length > 0 && (
                          <div className="text-xs text-zinc-500 mt-0.5 truncate">
                            {Object.entries(row.details).map(([k, v]) => `${k}=${v}`).join(' · ')}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 whitespace-nowrap">{formatDateTime(row.timestamp)}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="breakglass" className="mt-6">
            <PlatformBreakGlass />
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <PlatformLeads />
          </TabsContent>
        </Tabs>
      </main>

      {/* Organisation detail dialog */}
      <Dialog open={!!selectedOrg} onOpenChange={(v) => !v && setSelectedOrg(null)}>
        <DialogContent className="bg-[#17171E] border-zinc-800 text-zinc-100 max-w-2xl" data-testid="org-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">{selectedOrg?.name}</DialogTitle>
            <DialogDescription className="text-zinc-500">{selectedOrg?.slug}</DialogDescription>
          </DialogHeader>
          {!selectedDetail ? (
            <div className="py-6 text-center text-zinc-500">Loading…</div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-3">
                <StatusPill status={selectedDetail.status} />
                {selectedDetail.subscription?.status && (
                  <Badge variant="outline" className="border-zinc-700 text-zinc-300">
                    Subscription: {selectedDetail.subscription.status}
                  </Badge>
                )}
                {selectedDetail.suspended && (
                  <Badge variant="outline" className="border-rose-500/40 text-rose-400">Suspended</Badge>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-md border border-zinc-800">
                  <div className="text-[11px] uppercase text-zinc-500 font-semibold">Owner</div>
                  <div className="text-zinc-100">{selectedDetail.owner_name || '—'}</div>
                  <div className="text-zinc-400 text-xs">{selectedDetail.owner_email || '—'}</div>
                </div>
                <div className="p-3 rounded-md border border-zinc-800">
                  <div className="text-[11px] uppercase text-zinc-500 font-semibold">Created</div>
                  <div className="text-zinc-100">{formatDate(selectedDetail.created_at)}</div>
                </div>
                <div className="p-3 rounded-md border border-zinc-800">
                  <div className="text-[11px] uppercase text-zinc-500 font-semibold">Trial ends</div>
                  <div className="text-zinc-100">
                    {formatDate(selectedDetail.trial_ends_at)}
                    {selectedDetail.trial_days_remaining !== null && (
                      <span className="ml-2 text-xs text-zinc-500">({selectedDetail.trial_days_remaining}d)</span>
                    )}
                  </div>
                </div>
                <div className="p-3 rounded-md border border-zinc-800">
                  <div className="text-[11px] uppercase text-zinc-500 font-semibold">Last activity</div>
                  <div className="text-zinc-100">{formatDateTime(selectedDetail.last_activity_at)}</div>
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase text-zinc-500 font-semibold mb-2">Users ({selectedDetail.user_count})</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(selectedDetail.users_by_role || {}).map(([role, count]) => (
                    <Badge key={role} variant="outline" className="border-zinc-700 text-zinc-200">
                      {role}: <span className="ml-1 tabular-nums">{count}</span>
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase text-zinc-500 font-semibold mb-2">Usage counts</div>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(selectedDetail.usage || {}).map(([k, v]) => (
                    <div key={k} className="p-2 rounded-md border border-zinc-800 text-center">
                      <div className="text-[10px] uppercase text-zinc-500">{k}</div>
                      <div className="text-zinc-100 font-semibold tabular-nums">{v}</div>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-zinc-600 mt-2">Metadata only · tenant content never read.</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
